import { QuillEditorChangeEvent, CustomQuillEditorToolbarProps } from './types'
import { useCallback, useEffect, useRef, useState } from 'react'
import Quill, { DeltaStatic, Sources } from 'quill'
import React from 'react'
import classNames from 'classnames'
import { Tooltip } from 'antd'
import { EmojiManager } from '@pushly/aqe/lib/components/emoji-manager/emoji-manager-antd6'
import { MacroManager } from '@pushly/aqe/lib/components/macro-manager/macro-manager-antd6'
import { useDebouncer } from '@pushly/aqe/lib/hooks'
import { DEFAULT_MACRO_TRIGGER, MacroOption } from '@pushly/aqe/lib/components/macro-manager/macro-manager'
import { DEFAULT_MACRO_TYPES, getMacroInsertText } from '@pushly/aqe/lib/components/macro-manager/constants'
import { getInputContext } from '../macro-manager/helpers'
import { INLINE_TOGGLE_FORMATS, InlineToggleFormat } from './constants'

type InlineToggleState = Record<InlineToggleFormat, boolean>

const NO_INLINE_TOGGLES_ACTIVE: InlineToggleState = { bold: false, italic: false, strike: false }

const INLINE_TOGGLE_TITLES: Record<InlineToggleFormat, string> = {
    bold: 'Bold',
    italic: 'Italic',
    strike: 'Strikethrough',
}

/** Quill's own button glyphs, so a control it no longer owns still looks like one. */
const QUILL_ICONS: Record<string, Record<string, string>> = Quill.import('ui/icons')

/**
 * The block formats offered as a row of mutually exclusive options.
 *
 * These are driven entirely from here rather than by Quill. Quill derives both
 * what a click means and which option is lit from the button's `ql-active`
 * class, and for a set of options it cannot tell one button from another — it
 * only ever sees the value the class implies. So the four alignments shared one
 * verdict, and a repaint that had cleared the class turned "centre this" into
 * "clear the alignment".
 */
const BLOCK_OPTION_GROUPS = {
    align: [
        { value: '', title: 'Align left' },
        { value: 'center', title: 'Align centre' },
        { value: 'right', title: 'Align right' },
        { value: 'justify', title: 'Justify' },
    ],
    list: [
        { value: 'ordered', title: 'Numbered list' },
        { value: 'bullet', title: 'Bulleted list' },
    ],
} as const

type BlockOptionFormat = keyof typeof BLOCK_OPTION_GROUPS

type BlockOptionState = Record<BlockOptionFormat, string>

/** The default in each group is the absence of the format, which reads as ''. */
const NO_BLOCK_OPTIONS_ACTIVE: BlockOptionState = { align: '', list: '' }

const readBlockOptions = (editor: Quill, range: ReturnType<Quill['getSelection']>): BlockOptionState => {
    if (!range) {
        return NO_BLOCK_OPTIONS_ACTIVE
    }

    const formats = editor.getFormat(range)

    return {
        align: typeof formats.align === 'string' ? formats.align : '',
        list: typeof formats.list === 'string' ? formats.list : '',
    }
}

/**
 * Which of the on/off formats apply where the caret is.
 *
 * `range` is passed in rather than read here so this never forces focus — see
 * the note in the selection handler.
 */
const readInlineToggles = (editor: Quill, range: ReturnType<Quill['getSelection']>): InlineToggleState => {
    if (!range) {
        return NO_INLINE_TOGGLES_ACTIVE
    }

    const formats = editor.getFormat(range)

    return INLINE_TOGGLE_FORMATS.reduce(
        (state, format) => ({ ...state, [format]: !!formats[format] }),
        NO_INLINE_TOGGLES_ACTIVE,
    )
}

type ToolbarButtonProps = {
    className: string
    title: string
    value?: string
    /**
     * Set only for the on/off formats, which are lit from the document instead
     * of from Quill's `ql-active`. Left undefined by every other control, which
     * Quill lights for itself.
     */
    active?: boolean
    /** Re-read the document once Quill's own click handling has run. */
    onToggled?: () => void
}

/**
 * One of Quill's own toolbar controls, named on hover.
 *
 * Quill finds these by class and binds them itself, and it replaces whatever is
 * inside them with its own glyph — so the button stays empty here and the name
 * lives on the tooltip rather than in the markup.
 */
const ToolbarButton = ({ className, title, value, active, onToggled }: ToolbarButtonProps) => (
    <Tooltip title={title}>
        <button
            type="button"
            className={classNames(className, { 'is-active': active })}
            value={value}
            // Fires after Quill's own listener on this button, so the document
            // already carries the change by the time this reads it.
            onClick={onToggled}
            aria-label={title}
            // Keeps the editor focused through the click. Turning a format off
            // with nothing selected is stored against the cursor rather than
            // applied to any text, and that survives only while the cursor does
            // — a blur and refocus around the click can drop it, so the next
            // character typed comes out formatted after all. The custom controls
            // below already do this; Quill's own were relying on it not
            // mattering.
            onMouseDown={(ev) => ev.preventDefault()}
        ></button>
    </Tooltip>
)

type ToolbarOptionButtonProps = {
    /**
     * Styling hook only. Deliberately not a `ql-` name: Quill binds any button
     * in the container whose class names a format it knows, and these are bound
     * here instead.
     */
    className: string
    title: string
    icon: string
    active: boolean
    onSelect: () => void
}

/**
 * One option in a mutually exclusive group, drawn with Quill's own glyph.
 *
 * The markup is Quill's published icon for the format, so detaching these
 * controls from Quill costs nothing visually.
 */
const ToolbarOptionButton = ({ className, title, icon, active, onSelect }: ToolbarOptionButtonProps) => (
    <Tooltip title={title}>
        <button
            type="button"
            className={classNames(className, { 'is-active': active })}
            aria-label={title}
            aria-pressed={active}
            // Keeps the editor focused through the click, as the other custom
            // controls do — a block format still needs a selection to apply to.
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={onSelect}
            dangerouslySetInnerHTML={{ __html: icon }}
        />
    </Tooltip>
)

/**
 * Where a toolbar-driven insert should land.
 *
 * `getSelection(true)` is meant to focus the editor and hand back a range, but
 * it still answers `null` when the editor has never held a selection — so
 * reaching straight for `.index` throws on the first click, before the author
 * has typed anything. Falling back to the end of the document puts the
 * insertion where an author clicking a control on an empty field expects it.
 */
export const getInsertionRange = (editor: Quill): { index: number; length: number } => {
    const range = editor.getSelection(true)

    return {
        // getLength() counts the trailing newline Quill always keeps.
        index: range?.index ?? Math.max(editor.getLength() - 1, 0),
        length: range?.length ?? 0,
    }
}

export const CustomQuillEditorToolbar = (props: CustomQuillEditorToolbarProps) => {
    const { editorInstanceId, formats } = props

    const quill = useRef<Quill | null>(null)
    // The editor's visible box. Both pickers size their popup to it and align
    // their left edges with it, exactly as they do to an input elsewhere.
    const editorRef = useRef<HTMLElement | null>(null)
    const [showMacroOptions, _setShowMacroOptions] = useState<boolean>(false)
    // Which way the text at the cursor runs, so the pair can report which of the
    // two is in force.
    const [textDirection, setTextDirection] = useState<'' | 'rtl'>('')
    const [activeInlineToggles, setActiveInlineToggles] = useState<InlineToggleState>(NO_INLINE_TOGGLES_ACTIVE)
    const [activeBlockOptions, setActiveBlockOptions] = useState<BlockOptionState>(NO_BLOCK_OPTIONS_ACTIVE)

    // The cursor moving in or out of a `{{` trigger arrives as several events in
    // one tick, so the automatic path is debounced to keep the list from
    // flickering between them.
    const [debounce, cancelDebounce] = useDebouncer(120)
    const setShowMacroOptions = useCallback((v: boolean) => {
        debounce(() => {
            _setShowMacroOptions(v)
        })
    }, [])

    /**
     * Opening or closing the list from its own control.
     *
     * Immediate, and it drops whatever the automatic path had queued: clicking
     * the control also moves the editor's selection, and the debounced update
     * that follows would otherwise close the list a moment after it opened.
     */
    const handleMacroOpenChange = useCallback((next: boolean) => {
        cancelDebounce()
        _setShowMacroOptions(next)
    }, [])

    const inputContext = useRef(getInputContext(quill.current?.getText(), null, DEFAULT_MACRO_TRIGGER))

    useEffect(() => {
        if (props.quill.current) {
            quill.current = props.quill.current!.getEditor()
            editorRef.current = (quill.current.root.closest('.ql-container') as HTMLElement) ?? quill.current.root
        }
    }, [editorInstanceId, props.quill.current])

    // v1+ during text-change detect macro pattern match to open options for selection
    // logic below checks on text change for specific macro pattern {{
    const handleQuillTextChange = useCallback(
        (event: QuillEditorChangeEvent, delta: DeltaStatic) => {
            if (!showMacroOptions) {
                const startSelection = inputContext.current.cursor.cursorPos
                // >= 1 ensures must have min characters to satisfy "{{"
                if (startSelection !== undefined && startSelection >= 1) {
                    // quill auto appends a "\n" to text - we need to remove during pos calculations
                    const text = (quill.current!.getText(startSelection - 3) ?? '').replace(/\n$/, '')
                    const isClose = /}}$/.test(text)
                    // offsetting start index to account for nested captures
                    const startOffset = text.length >= 3 ? 1 : 0
                    const isNestedOpen = /[{]{3,}/.test(text)

                    const isDeleteDelta = !!delta.ops?.find((d) => 'delete' in d)
                    const shouldShow = text.startsWith('{{', startOffset) && !isNestedOpen
                    const shouldHide = isClose || (isDeleteDelta && !isNestedOpen && text !== '{{')

                    if (shouldShow || shouldHide) {
                        setShowMacroOptions(shouldShow)
                    }
                }
            }
        },
        [editorInstanceId, !!quill, showMacroOptions],
    )

    /**
     * Quill repaints `ql-active` asynchronously from `scroll-optimize`, and that
     * repaint loses a format held against a collapsed cursor — so a button whose
     * format really is on goes dark a fraction of a second after being clicked,
     * until the next caret move re-reads it. Reading the document here, once
     * Quill's click handling has run, is the same source of truth the toggle
     * handlers use.
     */
    const handleInlineToggleClick = useCallback(() => {
        const editor = quill.current

        if (editor) {
            setActiveInlineToggles(readInlineToggles(editor, editor.getSelection()))
        }
    }, [editorInstanceId])

    /**
     * Choose one option from a mutually exclusive group.
     *
     * Resolved against the document, like the on/off toggles: the option already
     * in force is cleared rather than re-applied, and clearing is how the default
     * of each group — left alignment, no list — is expressed.
     */
    const handleBlockOptionSelect = useCallback((format: BlockOptionFormat, value: string) => {
        const editor = quill.current
        const range = editor?.getSelection(true)

        if (!editor || !range) {
            return
        }

        const current = readBlockOptions(editor, range)[format]
        // A group's default is the absence of the format, so choosing it — or
        // choosing what is already in force — removes it rather than writing an
        // empty value the attributor would have to interpret.
        editor.format(format, value === '' || current === value ? false : value, 'user')

        setActiveBlockOptions(readBlockOptions(editor, editor.getSelection()))
    }, [])

    const handleQuillSelectionChange = useCallback(
        (_ev: QuillEditorChangeEvent) => {
            // Read the range without forcing focus, and hand it to getFormat so
            // that does not force either. Both getSelection(true) and the
            // no-argument getFormat() focus the editor and re-apply its saved
            // range as a side effect — which is the wrong thing to do from a
            // handler that merely observes a selection changing, and can disturb
            // state the editor is holding about what the next keystroke should
            // look like.
            const editor = quill.current!
            const range = editor.getSelection()

            inputContext.current = getInputContext(editor.getText(), range?.index ?? null, DEFAULT_MACRO_TRIGGER)

            // a focus event while already open should ignore inTrigger state
            setShowMacroOptions(inputContext.current.cursor.inTrigger)

            const activeDirection = range ? editor.getFormat(range).direction : undefined
            setTextDirection(activeDirection === 'rtl' ? 'rtl' : '')

            setActiveInlineToggles(readInlineToggles(editor, range))
            setActiveBlockOptions(readBlockOptions(editor, range))
        },
        [editorInstanceId, showMacroOptions],
    )

    useEffect(() => {
        const editor = quill.current

        if (editor) {
            editor.on(
                'editor-change',
                (
                    name: 'text-change' | 'selection-change',
                    deltaOrRange: any,
                    oldDeltaOrRange: any,
                    _source: Sources,
                ) => {
                    const isFocus = !!deltaOrRange && !oldDeltaOrRange
                    const isBlur = !deltaOrRange && !!oldDeltaOrRange
                    const event: QuillEditorChangeEvent = isBlur ? 'blur' : isFocus ? 'focus' : 'change'

                    switch (true) {
                        case name === 'text-change':
                            handleQuillTextChange(event, deltaOrRange)
                            break

                        case !isBlur:
                            handleQuillSelectionChange(event)
                            break
                    }
                },
            )
        }
    }, [editorInstanceId, showMacroOptions])

    // Emojis
    const handleEmojiSelection = useCallback(
        (emoji: string) => {
            const editor = quill.current

            if (editor) {
                const { index } = getInsertionRange(editor)
                editor.insertText(index, emoji, 'user')
                editor.setSelection(index + emoji.length, 0)
                editor.focus()
            }
        },
        [editorInstanceId],
    )

    const handleMacroSelection = useCallback(
        (macro: MacroOption) => {
            const editor = quill.current

            if (editor) {
                const [, tEnd] = DEFAULT_MACRO_TRIGGER.split('.')
                const macroValue = getMacroInsertText(macro, DEFAULT_MACRO_TRIGGER)

                const selectionStart = getInsertionRange(editor).index || inputContext.current.cursor.cursorPos
                let nextCursorPos = selectionStart + macroValue.length

                const activeMacro = inputContext.current.macro
                if (inputContext.current.cursor.inTrigger && activeMacro) {
                    const currStartPos = activeMacro.startPos
                    // end position should pad end for the closing boundary
                    const currEndPos =
                        activeMacro.endPos !== null
                            ? activeMacro.endPos + tEnd.length
                            : activeMacro.startPos + activeMacro.value.length + tEnd.length

                    // inline macro replacement
                    editor.deleteText(currStartPos, currEndPos - currStartPos, 'user')
                    editor.insertText(currStartPos, macroValue, 'user')
                    nextCursorPos = currStartPos + macroValue.length
                } else {
                    // Macro addition
                    editor.insertText(selectionStart, macroValue, 'user')
                }

                editor.setSelection(nextCursorPos, 0)
                editor.focus()

                setShowMacroOptions(false)
            }
        },
        [editorInstanceId, !!quill],
    )

    // Grouped rather than one long row of buttons: emphasis, lists, alignment and
    // insertion are separate jobs, and a reader scanning for one of them finds it
    // faster in a cluster of three than in a row of twelve. Quill's own markup
    // convention for a cluster is .ql-formats, so grouping costs nothing.
    //
    // Within a group each control keeps its own outline, because each is an
    // independent switch. Only a group whose options are mutually exclusive —
    // alignment, text direction — is joined into a single segmented control,
    // which is what tells the reader the options are one choice.
    const hasListFormats = formats.includes('list') || formats.includes('bullet')
    const hasInsertFormats = formats.includes('emojis') || formats.includes('macros')

    // Direction is drawn as two named options rather than Quill's own control,
    // which is a single button carrying both glyphs at once. Quill overwrites the
    // contents of any button whose class it recognises, so a labelled pair cannot
    // use `ql-direction` — these call the same format on the editor instead.
    const applyDirection = (next: '' | 'rtl') => {
        const editor = quill.current
        if (!editor) {
            return
        }

        // formatLine rather than format: direction belongs to the block, and the
        // block-scope call applies it to the lines the selection covers rather
        // than depending on the editor holding focus at the moment of the click.
        const { index, length } = getInsertionRange(editor)
        editor.formatLine(index, length, 'direction', next === 'rtl' ? 'rtl' : false, 'user')
        setTextDirection(next)
    }

    return (
        <div id={`${editorInstanceId}-toolbar`} className="ql-snow custom-quill-toolbar">
            <span className="ql-formats">
                {INLINE_TOGGLE_FORMATS.filter((format) => formats.includes(format)).map((format) => (
                    <ToolbarButton
                        key={format}
                        className={`ql-${format}`}
                        title={INLINE_TOGGLE_TITLES[format]}
                        active={activeInlineToggles[format]}
                        onToggled={handleInlineToggleClick}
                    />
                ))}
            </span>

            {hasListFormats && (
                <span className="ql-formats">
                    {BLOCK_OPTION_GROUPS.list
                        // 'bullet' is the format name this toolbar exposes for the
                        // unordered list, which Quill itself calls list=bullet.
                        .filter((option) => formats.includes(option.value === 'bullet' ? 'bullet' : 'list'))
                        .map((option) => (
                            <ToolbarOptionButton
                                key={option.value}
                                className="custom-quill-list"
                                title={option.title}
                                icon={QUILL_ICONS.list[option.value]}
                                active={activeBlockOptions.list === option.value}
                                onSelect={() => handleBlockOptionSelect('list', option.value)}
                            />
                        ))}
                </span>
            )}

            {formats.includes('indent') && (
                <span className="ql-formats">
                    <ToolbarButton className="ql-indent" value="-1" title="Decrease indent" />
                    <ToolbarButton className="ql-indent" value="+1" title="Increase indent" />
                </span>
            )}

            {formats.includes('align') && (
                <span className="ql-formats ql-formats-joined">
                    {BLOCK_OPTION_GROUPS.align.map((option) => (
                        <ToolbarOptionButton
                            key={option.value}
                            className="custom-quill-align"
                            title={option.title}
                            icon={QUILL_ICONS.align[option.value]}
                            active={activeBlockOptions.align === option.value}
                            onSelect={() => handleBlockOptionSelect('align', option.value)}
                        />
                    ))}
                </span>
            )}

            {formats.includes('direction') && (
                <span className="ql-formats ql-formats-joined ql-formats-direction">
                    <Tooltip title="Left to right">
                        <button
                            type="button"
                            className={classNames('ql-text-direction', { 'is-active': textDirection === '' })}
                            aria-label="Text direction left to right"
                            aria-pressed={textDirection === ''}
                            // Quill formats the current selection, and a button
                            // taking focus is a selection lost — its own toolbar
                            // suppresses the mousedown for the same reason.
                            onMouseDown={(ev) => ev.preventDefault()}
                            onClick={() => applyDirection('')}
                        >
                            LTR
                        </button>
                    </Tooltip>
                    <Tooltip title="Right to left">
                        <button
                            type="button"
                            className={classNames('ql-text-direction', { 'is-active': textDirection === 'rtl' })}
                            aria-label="Text direction right to left"
                            aria-pressed={textDirection === 'rtl'}
                            onMouseDown={(ev) => ev.preventDefault()}
                            onClick={() => applyDirection('rtl')}
                        >
                            RTL
                        </button>
                    </Tooltip>
                </span>
            )}

            {formats.includes('color') && (
                <span className="ql-formats">
                    <Tooltip title="Text colour">
                        <select className="ql-color" aria-label="Text color"></select>
                    </Tooltip>
                </span>
            )}

            {hasInsertFormats && (
                <span className="ql-formats ql-formats-insert">
                    {/* The same two pickers the templatized fields use, so a
                        macro list is the same list wherever it is opened in a
                        form. They name themselves here — unlike the icon-only
                        controls above, which carry tooltips instead.

                        editorRef sizes and left-aligns each popup to the editor,
                        the way those fields align theirs to the input. */}
                    {formats.includes('emojis') && (
                        <EmojiManager
                            className="ql-emoji-picker"
                            onSelect={handleEmojiSelection}
                            inputRef={editorRef}
                            label={<span className="ql-control-label">Emoji</span>}
                        />
                    )}

                    {formats.includes('macros') && (
                        <MacroManager
                            className="ql-macro-select"
                            open={showMacroOptions}
                            onOpenChange={handleMacroOpenChange}
                            types={props.macroTypes ?? DEFAULT_MACRO_TYPES}
                            customOptions={props.macroOptions}
                            validOptionTypes={[]}
                            onSelect={handleMacroSelection}
                            activeMacro={inputContext.current.macro?.value}
                            inputRef={editorRef}
                            label={<span className="ql-control-label">Personalize</span>}
                        />
                    )}
                </span>
            )}
        </div>
    )
}
